import { UseMutationResult, UseQueryResult, useMutation, useQuery } from '@tanstack/react-query'
import { useFetchAllRows, UseFetchAllRowsResult } from '@/hooks/useFetchAllRows'
import { API_COMMON_CACHE_TIME } from '@/utils/consts'
import { getShipperListV2 } from '@/services/shipper/apis'
import {
  confirmEtcOutbounds,
  getEtcOutbounds,
  getOutboundProgress,
  saveEtcOutbounds,
} from '@/services/outbound/outboundEtc/apis'
import {
  ETC_OUTBOUND_FETCH_SIZE,
  ETC_OUTBOUND_SEARCH_FAILED_MESSAGE,
  ETC_OUTBOUND_SEARCH_TYPE,
  PROGRESS_MAX_ATTEMPTS,
  PROGRESS_MAX_FAILURES,
  PROGRESS_POLLING_INTERVAL,
  PROGRESS_TERMINAL_STATUSES,
} from '@/services/outbound/outboundEtc/consts'
import {
  EtcOutboundConfirmBody,
  EtcOutboundConfirmRes,
  EtcOutboundSaveBody,
  EtcOutboundSaveRes,
  EtcOutboundRow,
  EtcOutboundSearchBody,
  OutboundProgress,
} from '@/services/outbound/outboundEtc/types'

const PATHS = ['OUTBOUND', 'ETC', 'gi38']

export const outboundEtcQueryKeys = {
  all: PATHS,
  list: (body: EtcOutboundListBody) => [...PATHS, 'LIST', body] as const,
  progress: (progressKey: string) => [...PATHS, 'PROGRESS', progressKey] as const,
  shippers: [...PATHS, 'SHIPPERS'] as const,
}

export interface EtcOutboundShipperOption {
  /** 화주코드(SPR_NM) — 조회·저장이 쓰는 값이다. */
  value: string
  /** 화주명 */
  label: string
  logisticsServiceType: string
}

/**
 * 화주 목록 — `POST /api/v1/get-shippers` 전건.
 *
 * 필터의 화주유형/화주명 셀렉트와 표의 화주코드 편집 셀이 같은 목록을 쓴다.
 * 클라이언트 검색 전제로 한 번에 받는다(ST10·ST11·EST05 선례의 size=10000).
 */
export const useGetEtcOutboundShippers = (): { options: EtcOutboundShipperOption[]; isFetching: boolean; error: Error | null; errorUpdatedAt: number } => {
  const { data, isFetching, error, errorUpdatedAt } = useQuery({
    queryKey: outboundEtcQueryKeys.shippers,
    queryFn: async (): Promise<EtcOutboundShipperOption[]> => {
      const res = await getShipperListV2({ shipperNames: [], logisticsServiceTypes: [], page: 0, size: 10000 })
      return (res?.data?.items ?? []).map((s) => ({
        value: s.shipperCode,
        label: s.shipperName || s.shipperCode,
        logisticsServiceType: s.logisticsServiceType,
      }))
    },
    staleTime: API_COMMON_CACHE_TIME,
    // 실패 안내는 화면이 신규 디자인 알럿(PboAlert)으로 띄운다 — 전역 알럿(옛 mwms-ds 모달)은 끈다.
    meta: { noErrorMessage: true },
  })

  return { options: data ?? [], isFetching, error, errorUpdatedAt }
}

/** 조회 조건 — BE 구간(page·pageSize·type)은 훅이 채운다. 키에 넣지 않아 페이지를 넘겨도 다시 받지 않는다. */
export type EtcOutboundListBody = Omit<EtcOutboundSearchBody, 'page' | 'pageSize' | 'type'>

/** 구간 경계에서 겹친 행을 거르는 기본키 — 모듈 상수라 참조가 바뀌지 않는다. 조회 행은 늘 id 가 있다(신규 행만 없다). */
const rowIdOf = (row: EtcOutboundRow): string | number => row.id ?? `${row.ORD_NO}-${row.ORD_OPT_NO}`

/**
 * 기타출고 목록 — 전량 조회(LOGISPRD-15268). BE 한 번 상한(10,000)씩 끊어 받는다.
 *
 * 조회 조건(화주코드)이 있으면 마운트에서 바로 돈다 — 다른 메뉴를 다녀와 조건이 복원된 경우가 그렇다.
 * 최초 진입은 조건이 없어 돌지 않는다(1.0 도 진입 시 빈 그리드였다). 화주 없이 조회하면 BE 가 400 으로 막는다.
 *
 * BE 는 0건도 `rtnChk:false` + 안내 문구로 준다(`EtcOutboundOrderResponse.empty`, params 있음) — 첫 구간이면 빈 결과로 돌려준다.
 * 뒤 구간에서 오면 실패로 올린다 — 빈 결과로 두면 훅이 "짧은 구간" 으로 보고 받기를 끝내 일부만 받은 목록으로 저장·확정이 열린다.
 *  (받는 사이 데이터가 줄었을 때도 여기로 온다 — 다시 조회하면 된다.)
 * 실패(`error`, params 없음)는 예외로 올리고 문구를 `response.data.message` 에 싣는다 — 실패 알럿은 공용 훅 meta(errorToast)로 전역이 띄운다.
 */
export const useGetEtcOutbounds = (body: EtcOutboundListBody): UseFetchAllRowsResult<EtcOutboundRow> =>
  useFetchAllRows({
    queryKey: outboundEtcQueryKeys.list(body),
    // BE page 는 1부터 센다(PageRequest.of(page - 1, …)). 훅은 0부터 넘긴다.
    fetchPage: async (page, size) => {
      const res = await getEtcOutbounds({ ...body, type: page ? ETC_OUTBOUND_SEARCH_TYPE.next : ETC_OUTBOUND_SEARCH_TYPE.first, page: page + 1, pageSize: size ?? ETC_OUTBOUND_FETCH_SIZE })
      if (!res.rtnChk) {
        if (res.params && page === 0) return { items: [], totalCount: 0 }
        const message = (!res.params && res.message) || ETC_OUTBOUND_SEARCH_FAILED_MESSAGE
        throw Object.assign(new Error(message), { response: { data: { message } } })
      }
      const items = res.rtnData ?? []
      return { items, totalCount: res.params?.totalCnt ?? items.length }
    },
    chunkSize: ETC_OUTBOUND_FETCH_SIZE,
    getKey: rowIdOf,
    enabled: Boolean(body.params.SPR_NM),
  })

/** 저장. 중복(400)·SKU 미해소(422)는 reject 로 오므로 호출부가 error 에서 응답 본문을 꺼낸다. */
export const useSaveEtcOutbounds = (): UseMutationResult<EtcOutboundSaveRes, Error, EtcOutboundSaveBody> =>
  useMutation({
    mutationFn: (body: EtcOutboundSaveBody) => saveEtcOutbounds(body),
  })

/** 확정. SKU 미해소(422)만 reject 이고 나머지 실패는 200 + `result:false` 로 온다(1.0 계약). */
export const useConfirmEtcOutbounds = (): UseMutationResult<EtcOutboundConfirmRes, Error, EtcOutboundConfirmBody> =>
  useMutation({
    mutationFn: (body: EtcOutboundConfirmBody) => confirmEtcOutbounds(body),
  })

/**
 * 다음 폴링까지의 간격. 멈춰야 하면 false 다.
 *
 * 훅 밖으로 뺀 이유는 테스트에서 종료 조건을 직접 단정하기 위해서다 — 10초 간격을 실제로
 * 기다리는 테스트는 못 쓴다.
 */
export const progressRefetchInterval = (query: {
  state: { data?: OutboundProgress | null; errorUpdateCount?: number; dataUpdateCount?: number }
}): number | false => {
  const progress = query.state.data
  if (PROGRESS_TERMINAL_STATUSES.includes(progress?.status ?? '')) return false

  // 1.0 은 status 가 COMPLETED 로 바뀌기 전이어도 진행률이 100 에 닿으면 끝낸다
  // (grid.js:869 `status === 'COMPLETED' || progress >= 100`). 워커가 100% 를 찍고 상태 갱신이
  // 늦는 구간에서 계속 도는 걸 막는다. total 이 0 이면 0/0 을 완료로 오판하므로 제외한다.
  if ((progress?.total ?? 0) > 0 && (progress?.processed ?? 0) >= (progress?.total ?? 0)) return false

  // 실패 횟수는 `errorUpdateCount` 로 센다. `fetchFailureCount` 를 쓰면 안 된다 — 요청이 시작될
  // 때마다 0 으로 되돌아가고(query-core `query.js:351` fetchState), 전역 `retry: 0`
  // (utils/queryClient.ts:8)이라 폴링 1회당 최대 1 이다. 5 에 닿을 수 없어 상한이 성립하지 않는다.
  // `errorUpdateCount` 는 오류마다 +1 이고 fetch 가 리셋하지 않는다(같은 파일 :321).
  if ((query.state.errorUpdateCount ?? 0) >= PROGRESS_MAX_FAILURES) return false

  // 마지막 방어선. 위 조건이 전부 정상 응답에 막혀 못 끊는 경우가 있다(워커 사망 + RUNNING 200 반복).
  // 성공만 세면 계속 실패하는 구간을 못 끊으므로 성공·실패를 합쳐 시도 횟수로 본다.
  if ((query.state.dataUpdateCount ?? 0) + (query.state.errorUpdateCount ?? 0) >= PROGRESS_MAX_ATTEMPTS) return false

  return PROGRESS_POLLING_INTERVAL
}

/**
 * 저장 진행률 폴링. `progressKey` 가 있는 동안만 돈다.
 *
 * 멈추는 조건이 두 가지다. 1.0 `progressPollingEpic`(grid.js:863)의 종료 조건과 같다.
 * 1. 상태가 COMPLETED·FAILED — 작업이 끝났다
 * 2. 실패가 상한(5회)에 닿음 — 엔드포인트가 계속 죽어 있으면 무한히 요청하지 않는다
 *
 * `refetchInterval` 을 고정 숫자로 두면 안 된다. 끝난 뒤에도 계속 돌고, 호출부가 progressKey 를
 * 비우는 경로가 실패하면 요청이 멎지 않는다.
 */
export const useGetOutboundProgress = (progressKey: string | null): UseQueryResult<OutboundProgress | null, Error> =>
  useQuery({
    queryKey: outboundEtcQueryKeys.progress(progressKey ?? ''),
    queryFn: async () => (await getOutboundProgress(progressKey as string)).data ?? null,
    enabled: Boolean(progressKey),
    gcTime: 0,
    refetchInterval: (query) => progressRefetchInterval(query),
    // 폴링이라 실패가 연속으로 난다. 전역 알럿을 끄지 않으면 상한(5회)까지 알럿이 다섯 번 뜬다.
    // 사용자에게는 화면이 한 번만 알린다(`MESSAGES.progressFailed`).
    meta: { noErrorMessage: true },
  })
